import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { resolveRouteSEO, applyDOMMetaTags } from '../utils/seo'

function clampAllowedValues(val, allowedArray, defaultVal) {
    const num = parseInt(val, 10);
    return allowedArray.includes(num) ? num : defaultVal;
}

export const useSettingsStore = defineStore('settings', () => {
    // Store Profile Settings
    const storeName = ref('')
    const storeDescription = ref('')
    const storeLogo = ref('')
    const storeFavicon = ref('')
    const storeAddress = ref('')
    const storeTaxId = ref('')
    const storePhone = ref('')
    const storeEmail = ref('')
    
    // SEO Settings
    const seoGlobalTitle = ref('Outdoor Lander — บ้านเก็บของสำเร็จรูป โรงเรือน โกดังเก็บของ คุณภาพพรีเมียม')
    const seoGlobalDescription = ref('ผู้นำด้านบ้านเก็บของสำเร็จรูป โรงเรือน โกดังเก็บของ')
    const seoGlobalKeywords = ref('บ้านเก็บของ, โรงเรือน, โกดังเก็บของ, Outdoor Lander')
    const seoGlobalOgImage = ref('/og-image.jpg')
    const seoStaticPages = ref([])
    
    // Warehouse Settings
    const warehouseLat = ref('')
    const warehouseLng = ref('')

    const isOnlineShoppingEnabled = ref(true)
    const isWishlistEnabled = ref(true)
    const isCompareEnabled = ref(true)
    const showDiscountBadge = ref(true)
    const isAiConsultantEnabled = ref(true)
    const showProductRating = ref(true)
    const showProductReview = ref(true)
    const productSortBy = ref('sort_order')
    const savedDesktopCols = typeof window !== 'undefined' ? localStorage.getItem('ol_product_columns_desktop') : null
    const savedMobileCols = typeof window !== 'undefined' ? localStorage.getItem('ol_product_columns_mobile') : null
    const productColumnsDesktop = ref(clampAllowedValues(savedDesktopCols, [2, 3, 4, 5], 3))
    const productColumnsMobile = ref(clampAllowedValues(savedMobileCols, [1, 2], 1))
    const productsPerPage = ref(12)

    // Dynamic Responsive Product Grid Classes for ALL store pages (2, 3, 4, 5 columns)
    const productGridClass = computed(() => {
        const desktopCols = Number(productColumnsDesktop.value) || 3
        const mobileCols = Number(productColumnsMobile.value) || 1

        if (mobileCols === 2) {
            if (desktopCols === 2) return 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-3 sm:gap-6'
            if (desktopCols === 4) return 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5'
            if (desktopCols === 5) return 'grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4'
            return 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6'
        } else {
            if (desktopCols === 2) return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6'
            if (desktopCols === 4) return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5'
            if (desktopCols === 5) return 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4'
            return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
        }
    })
    
    // Maintenance & Holiday Modes
    const maintenanceModeEnabled = ref(false)
    const maintenanceMessage = ref('ขออภัยค่ะ ขณะนี้เว็บไซต์อยู่ระหว่างการปรับปรุงระบบชั่วคราว กรุณาติดต่อทางไลน์หรือโทรศัพท์')
    const holidayModeEnabled = ref(false)
    const holidayMessage = ref('ร้านค้าอยู่ในช่วงวันหยุดยาว การจัดส่งอาจมีระยะเวลานานกว่าปกติ ขออภัยในความไม่สะดวกค่ะ')
    const holidayName = ref('')
    const holidayStartDate = ref('')  // ISO date string YYYY-MM-DD
    const holidayEndDate = ref('')    // ISO date string YYYY-MM-DD
    const holidayImage = ref('')      // URL of banner image

    // Auto-activate holiday mode if today is within the scheduled range
    const isHolidayActive = computed(() => {
        if (!holidayModeEnabled.value) return false
        // If no schedule set, just use the enabled toggle
        if (!holidayStartDate.value || !holidayEndDate.value) return true
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const start = new Date(holidayStartDate.value)
        start.setHours(0, 0, 0, 0)
        const end = new Date(holidayEndDate.value)
        end.setHours(23, 59, 59, 999)
        return today >= start && today <= end
    })

    const paymentPromptpayEnabled = ref(false)
    const paymentBankTransferEnabled = ref(false)
    const paymentIpayEnabled = ref(false)

    // Bank details (Array of objects)
    const paymentBankAccounts = ref([])

    // PromptPay details
    const paymentPromptpayNumber = ref('')

    // Shipping Restrictions (Array of strings)
    const shippingRestrictedProvinces = ref([])

    // Free Shipping Provinces (Array of province names)
    const freeShippingProvinces = ref(['กรุงเทพมหานคร', 'นนทบุรี', 'ปทุมธานี', 'สมุทรปราการ', 'สมุทรสาคร', 'นครปฐม'])

    // Free Installation Provinces (Array of province names)
    const freeInstallProvinces = ref(['กรุงเทพมหานคร', 'นนทบุรี', 'ปทุมธานี', 'สมุทรปราการ', 'สมุทรสาคร', 'นครปฐม'])

    // Installation Service & Calculation Engine Settings
    const isInstallationServiceEnabled = ref(true)
    const isInstallationAutoCalcEnabled = ref(true)
    const installationNoticeText = ref('')
    const installationFreeThreshold = ref(0)

    // Contact Channels
    const contactPhones = ref([])
    const contactPhone = ref('')
    const contactEmails = ref([])
    const contactEmail = ref('')
    const contactLines = ref([])
    const contactFacebookUrl = ref('')
    const contactTiktokUrl = ref('')
    const contactYoutubeUrl = ref('')
    const contactMapUrl = ref('')
    const contactMapEmbed = ref('')
    const contactAddress = ref('')
    const contactCompanyName = ref('')

    // Auto-resolve Google Maps link from direct URL, Embed iframe, address or warehouse coords
    const resolvedContactMapUrl = computed(() => {
        let raw = (contactMapUrl.value || '').trim()
        if (raw.includes('<iframe')) {
            const m = raw.match(/src=["']([^"']+)["']/i)
            if (m && m[1]) raw = m[1].trim()
        }
        if (raw.includes('/maps/embed')) {
            const cidMatch = raw.match(/!1s0x[0-9a-f]+(?:%3A|:)0x([0-9a-f]+)/i)
            if (cidMatch && cidMatch[1]) {
                try {
                    return `https://maps.google.com/?cid=${BigInt('0x' + cidMatch[1]).toString()}`
                } catch (e) {}
            }
            const coordMatch = raw.match(/!2d([0-9.-]+)!3d([0-9.-]+)/)
            if (coordMatch) {
                return `https://www.google.com/maps/search/?api=1&query=${coordMatch[2]},${coordMatch[1]}`
            }
        }
        if (raw) {
            return raw
        }
        if (contactMapEmbed.value && contactMapEmbed.value.trim()) {
            const embed = contactMapEmbed.value.trim()
            // 1. Try extracting CID hex (!1s0x...:0x[CID])
            const cidMatch = embed.match(/!1s0x[0-9a-f]+(?:%3A|:)0x([0-9a-f]+)/i)
            if (cidMatch && cidMatch[1]) {
                try {
                    const cidDec = BigInt('0x' + cidMatch[1]).toString()
                    return `https://maps.google.com/?cid=${cidDec}`
                } catch (e) {}
            }
            // 2. Try extracting coordinates (!2d[lng]!3d[lat])
            const coordMatch = embed.match(/!2d([0-9.-]+)!3d([0-9.-]+)/)
            if (coordMatch) {
                return `https://www.google.com/maps/search/?api=1&query=${coordMatch[2]},${coordMatch[1]}`
            }
            // 3. Fallback to embed url if valid web link
            if (embed.startsWith('http://') || embed.startsWith('https://')) {
                return embed
            }
        }
        if (contactAddress.value && contactAddress.value.trim()) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(contactAddress.value.trim())}`
        }
        if (storeAddress.value && storeAddress.value.trim()) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(storeAddress.value.trim())}`
        }
        if (warehouseLat.value && warehouseLng.value) {
            return `https://www.google.com/maps/search/?api=1&query=${warehouseLat.value},${warehouseLng.value}`
        }
        return ''
    })

    const initializeSettings = (pubSettings) => {
        if (!pubSettings) return

        if (pubSettings.online_shopping_enabled !== undefined) {
            isOnlineShoppingEnabled.value = String(pubSettings.online_shopping_enabled) === 'true'
        }
        if (pubSettings.wishlist_enabled !== undefined) {
            isWishlistEnabled.value = String(pubSettings.wishlist_enabled) === 'true'
        }
        if (pubSettings.compare_enabled !== undefined) {
            isCompareEnabled.value = String(pubSettings.compare_enabled) === 'true'
        }
        if (pubSettings.show_discount_badge !== undefined) {
            showDiscountBadge.value = String(pubSettings.show_discount_badge) === 'true'
        }
        if (pubSettings.ai_consultant_enabled !== undefined) {
            isAiConsultantEnabled.value = String(pubSettings.ai_consultant_enabled) === 'true'
        }

        if (pubSettings.show_product_rating !== undefined) {
            showProductRating.value = String(pubSettings.show_product_rating) === 'true'
        }
        if (pubSettings.show_product_review !== undefined) {
            showProductReview.value = String(pubSettings.show_product_review) === 'true'
        }
        if (pubSettings.product_sort_by !== undefined) {
            productSortBy.value = pubSettings.product_sort_by || 'sort_order'
        }
        if (pubSettings.product_columns_desktop !== undefined) {
            productColumnsDesktop.value = clampAllowedValues(pubSettings.product_columns_desktop, [2, 3, 4, 5], 3)
            if (typeof window !== 'undefined') {
                try { localStorage.setItem('ol_product_columns_desktop', productColumnsDesktop.value.toString()) } catch(e) {}
            }
        }
        if (pubSettings.product_columns_mobile !== undefined) {
            productColumnsMobile.value = clampAllowedValues(pubSettings.product_columns_mobile, [1, 2], 1)
            if (typeof window !== 'undefined') {
                try { localStorage.setItem('ol_product_columns_mobile', productColumnsMobile.value.toString()) } catch(e) {}
            }
        }
        if (pubSettings.products_per_page !== undefined) {
            productsPerPage.value = parseInt(pubSettings.products_per_page, 10) || 12
        }

        if (pubSettings.maintenance_mode_enabled !== undefined) {
            maintenanceModeEnabled.value = String(pubSettings.maintenance_mode_enabled) === 'true'
        }
        if (pubSettings.maintenance_message !== undefined) {
            maintenanceMessage.value = pubSettings.maintenance_message
        }
        if (pubSettings.holiday_mode_enabled !== undefined) {
            holidayModeEnabled.value = String(pubSettings.holiday_mode_enabled) === 'true'
        }
        if (pubSettings.holiday_message !== undefined) {
            holidayMessage.value = pubSettings.holiday_message
        }
        if (pubSettings.holiday_name !== undefined) {
            holidayName.value = pubSettings.holiday_name
        }
        if (pubSettings.holiday_start_date !== undefined) {
            holidayStartDate.value = pubSettings.holiday_start_date
        }
        if (pubSettings.holiday_end_date !== undefined) {
            holidayEndDate.value = pubSettings.holiday_end_date
        }
        if (pubSettings.holiday_image !== undefined) {
            holidayImage.value = pubSettings.holiday_image
        }

        // Store Settings
        if (pubSettings.store_name !== undefined) storeName.value = pubSettings.store_name || 'Outdoor Lander';
        if (pubSettings.store_description !== undefined) storeDescription.value = pubSettings.store_description;
        if (pubSettings.store_logo !== undefined) storeLogo.value = pubSettings.store_logo;
        if (pubSettings.store_favicon !== undefined) storeFavicon.value = pubSettings.store_favicon;
        if (pubSettings.store_address !== undefined) storeAddress.value = pubSettings.store_address;
        if (pubSettings.store_tax_id !== undefined) storeTaxId.value = pubSettings.store_tax_id;
        if (pubSettings.store_phone !== undefined) storePhone.value = pubSettings.store_phone;
        if (pubSettings.store_email !== undefined) storeEmail.value = pubSettings.store_email;
        
        // SEO Settings Initialization
        if (pubSettings.seo_global_title !== undefined) seoGlobalTitle.value = pubSettings.seo_global_title;
        if (pubSettings.seo_global_description !== undefined) seoGlobalDescription.value = pubSettings.seo_global_description;
        if (pubSettings.seo_global_keywords !== undefined) seoGlobalKeywords.value = pubSettings.seo_global_keywords;
        if (pubSettings.seo_global_og_image !== undefined) seoGlobalOgImage.value = pubSettings.seo_global_og_image;
        if (pubSettings.seo_static_pages !== undefined) {
            try {
                seoStaticPages.value = typeof pubSettings.seo_static_pages === 'string'
                    ? JSON.parse(pubSettings.seo_static_pages)
                    : pubSettings.seo_static_pages;
            } catch (e) {
                console.error('Failed to parse seo_static_pages in store:', e);
                seoStaticPages.value = [];
            }
        }
        
        if (pubSettings.warehouse_lat !== undefined) warehouseLat.value = pubSettings.warehouse_lat;
        if (pubSettings.warehouse_lng !== undefined) warehouseLng.value = pubSettings.warehouse_lng;

        // Contact Channels
        if (pubSettings.contact_phone !== undefined) contactPhone.value = pubSettings.contact_phone;
        if (pubSettings.contact_email !== undefined) contactEmail.value = pubSettings.contact_email;
        if (!contactEmail.value && pubSettings.store_email) contactEmail.value = pubSettings.store_email;
        if (pubSettings.contact_phones) {
            try { const p = typeof pubSettings.contact_phones === 'string' ? JSON.parse(pubSettings.contact_phones) : pubSettings.contact_phones; if (p.length) contactPhones.value = p } catch (e) {}
        }
        if (pubSettings.contact_emails) {
            try { const e = typeof pubSettings.contact_emails === 'string' ? JSON.parse(pubSettings.contact_emails) : pubSettings.contact_emails; if (e.length) contactEmails.value = e } catch (e) {}
        }
        if ((!contactEmails.value || contactEmails.value.length === 0) && (pubSettings.contact_email || pubSettings.store_email)) {
            contactEmails.value = [{ name: 'อีเมล', value: pubSettings.contact_email || pubSettings.store_email }]
        }
        if (pubSettings.contact_lines) {
            try { const l = typeof pubSettings.contact_lines === 'string' ? JSON.parse(pubSettings.contact_lines) : pubSettings.contact_lines; if (l.length) contactLines.value = l } catch (e) {}
        }
        if (pubSettings.contact_facebook_url !== undefined) contactFacebookUrl.value = pubSettings.contact_facebook_url;
        if (pubSettings.contact_tiktok_url !== undefined) contactTiktokUrl.value = pubSettings.contact_tiktok_url;
        if (pubSettings.contact_youtube_url !== undefined) contactYoutubeUrl.value = pubSettings.contact_youtube_url;
        if (pubSettings.contact_map_url !== undefined) contactMapUrl.value = pubSettings.contact_map_url;
        if (pubSettings.contact_map_embed !== undefined) contactMapEmbed.value = pubSettings.contact_map_embed;
        if (pubSettings.contact_address !== undefined) contactAddress.value = pubSettings.contact_address;
        if (pubSettings.contact_company_name !== undefined) contactCompanyName.value = pubSettings.contact_company_name;

        // Apply SEO and Favicon dynamically to DOM upon settings initialization
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/admin')) {
            const seoData = resolveRouteSEO(
                { path: window.location.pathname },
                {
                    storeName: storeName.value,
                    seoGlobalTitle: seoGlobalTitle.value,
                    seoGlobalDescription: seoGlobalDescription.value,
                    seoGlobalKeywords: seoGlobalKeywords.value,
                    seoGlobalOgImage: seoGlobalOgImage.value,
                    seoStaticPages: seoStaticPages.value
                }
            );
            applyDOMMetaTags(seoData, window.location.pathname);
        }
        if (storeFavicon.value && typeof document !== 'undefined') {
            const links = document.querySelectorAll("link[rel*='icon']");
            if (links.length > 0) {
                links.forEach(l => { l.href = storeFavicon.value; });
            } else {
                const link = document.createElement('link');
                link.rel = 'icon';
                link.href = storeFavicon.value;
                document.head.appendChild(link);
            }
        }

        if (pubSettings.payment_promptpay_enabled !== undefined) {
            paymentPromptpayEnabled.value = String(pubSettings.payment_promptpay_enabled) === 'true'
        }
        if (pubSettings.payment_bank_transfer_enabled !== undefined) {
            paymentBankTransferEnabled.value = String(pubSettings.payment_bank_transfer_enabled) === 'true'
        }
        if (pubSettings.payment_ipay_enabled !== undefined) {
            paymentIpayEnabled.value = String(pubSettings.payment_ipay_enabled) === 'true'
        }

        if (pubSettings.payment_bank_accounts) {
            try {
                paymentBankAccounts.value = typeof pubSettings.payment_bank_accounts === 'string'
                    ? JSON.parse(pubSettings.payment_bank_accounts)
                    : pubSettings.payment_bank_accounts;
            } catch (e) {
                console.error('Failed to parse payment_bank_accounts:', e);
                paymentBankAccounts.value = [];
            }
        }

        if (pubSettings.payment_promptpay_number !== undefined) {
            paymentPromptpayNumber.value = pubSettings.payment_promptpay_number
        }

        if (pubSettings.shipping_restricted_provinces) {
            try {
                shippingRestrictedProvinces.value = typeof pubSettings.shipping_restricted_provinces === 'string'
                    ? JSON.parse(pubSettings.shipping_restricted_provinces)
                    : pubSettings.shipping_restricted_provinces;
                if (!Array.isArray(shippingRestrictedProvinces.value)) {
                    shippingRestrictedProvinces.value = [];
                }
            } catch (e) {
                console.error('Failed to parse shipping_restricted_provinces:', e);
                shippingRestrictedProvinces.value = [];
            }
        }

        if (pubSettings.free_shipping_provinces) {
            try {
                const parsed = typeof pubSettings.free_shipping_provinces === 'string'
                    ? JSON.parse(pubSettings.free_shipping_provinces)
                    : pubSettings.free_shipping_provinces;
                if (Array.isArray(parsed) && parsed.length > 0) {
                    freeShippingProvinces.value = parsed;
                }
            } catch (e) {
                console.error('Failed to parse free_shipping_provinces:', e);
            }
        }

        if (pubSettings.free_install_provinces) {
            try {
                const parsed = typeof pubSettings.free_install_provinces === 'string'
                    ? JSON.parse(pubSettings.free_install_provinces)
                    : pubSettings.free_install_provinces;
                if (Array.isArray(parsed) && parsed.length > 0) {
                    freeInstallProvinces.value = parsed;
                }
            } catch (e) {
                console.error('Failed to parse free_install_provinces:', e);
            }
        }

        if (pubSettings.installation_service_enabled !== undefined) {
            isInstallationServiceEnabled.value = String(pubSettings.installation_service_enabled) !== 'false';
        }
        if (pubSettings.installation_auto_calc_enabled !== undefined) {
            isInstallationAutoCalcEnabled.value = String(pubSettings.installation_auto_calc_enabled) !== 'false';
        }
        if (pubSettings.installation_notice_text !== undefined) {
            installationNoticeText.value = pubSettings.installation_notice_text || '';
        }
        if (pubSettings.installation_free_threshold !== undefined) {
            installationFreeThreshold.value = parseFloat(pubSettings.installation_free_threshold) || 0;
        }
    }

    const fetchSettings = async () => {
        try {
            const res = await fetch('/api/settings/public?t=' + Date.now(), { cache: 'no-store' })
            const data = await res.json()
            if (data.success && data.data) {
                initializeSettings(data.data)
                return data.data
            }
        } catch (e) {
            console.error('Failed to fetch public settings in store:', e)
        }
    }

    return {
        storeName,
        storeDescription,
        storeLogo,
        storeFavicon,
        storeAddress,
        storeTaxId,
        storePhone,
        storeEmail,
        warehouseLat,
        warehouseLng,
        isOnlineShoppingEnabled,
        isWishlistEnabled,
        isCompareEnabled,
        showDiscountBadge,
        isAiConsultantEnabled,
        paymentPromptpayEnabled,
        paymentBankTransferEnabled,
        paymentIpayEnabled,
        paymentBankAccounts,
        paymentPromptpayNumber,
        shippingRestrictedProvinces,
        freeShippingProvinces,
        freeInstallProvinces,
        isInstallationServiceEnabled,
        isInstallationAutoCalcEnabled,
        installationNoticeText,
        installationFreeThreshold,
        showProductRating,
        showProductReview,
        productSortBy,
        productColumnsDesktop,
        productColumnsMobile,
        productsPerPage,
        productGridClass,
        maintenanceModeEnabled,
        maintenanceMessage,
        holidayModeEnabled,
        holidayMessage,
        holidayName,
        holidayStartDate,
        holidayEndDate,
        isHolidayActive,
        holidayImage,
        contactPhones,
        contactPhone,
        contactEmails,
        contactEmail,
        contactLines,
        contactFacebookUrl,
        contactTiktokUrl,
        contactYoutubeUrl,
        contactMapUrl,
        contactMapEmbed,
        contactAddress,
        contactCompanyName,
        resolvedContactMapUrl,
        seoGlobalTitle,
        seoGlobalDescription,
        seoGlobalKeywords,
        seoGlobalOgImage,
        seoStaticPages,
        initializeSettings,
        fetchSettings
    }
})
